import { useRef, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';

import useAuthProvider from 'store/authProvider/useAuthProvider';
import { isDevelop } from 'common/utils/helpers/remoteLogger';

const EVENTS = [
  'click',
  'dblclick',
  'mousewheel',
  'contextmenu',

  'touchstart',
  'touchmove',

  'keypress',

  'scroll',
  'resize',
];

const hours = 8;
const LOGOUT_DELAY = hours * 60 * 60 * 1000;
const CHECK_INTERVAL = 60 * 1000;

const useActivityHandler = () => {
  const elementRef = useRef(null);
  const { logout } = useAuthProvider();

  const lastActivityRef = useRef(Date.now());

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const checkInactivity = useCallback(() => {
    const now = Date.now();
    const inactiveFor = now - lastActivityRef.current;

    if (inactiveFor >= LOGOUT_DELAY) {
      logout();
    }
  }, [logout]);

  const debouncedMarkActivity = useMemo(
    () => debounce(markActivity, 300),
    [markActivity]
  );

  const listenActivity = useCallback(() => {
    const element = elementRef.current;

    if (!element) return () => null;

    EVENTS.forEach((eventName) => {
      element.addEventListener(eventName, debouncedMarkActivity);
    });

    document.addEventListener('visibilitychange', checkInactivity);
    window.addEventListener('focus', checkInactivity);
    window.addEventListener('pageshow', checkInactivity);

    const intervalId = window.setInterval(checkInactivity, CHECK_INTERVAL);

    return () => {
      EVENTS.forEach((eventName) => {
        element.removeEventListener(eventName, debouncedMarkActivity);
      });

      document.removeEventListener('visibilitychange', checkInactivity);
      window.removeEventListener('focus', checkInactivity);
      window.removeEventListener('pageshow', checkInactivity);

      window.clearInterval(intervalId);
      debouncedMarkActivity.cancel();
    };
  }, [checkInactivity, debouncedMarkActivity]);

  useEffect(() => {
    if (isDevelop) {
      return () => null;
    }

    markActivity();
    return listenActivity();
  }, [listenActivity, markActivity]);

  return elementRef;
};

export default useActivityHandler;
